import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Flame, LogOut, Dumbbell, LineChart as LineIcon, Apple, RefreshCw, CheckCircle2, Circle, Zap, Sparkles, Lock, FileDown, CalendarPlus, Footprints, Check, Activity, Settings as SettingsIcon, ClipboardList } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import api, { formatError, track } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { AlertTriangle } from "lucide-react";
import { formatFrDate } from "../lib/date";
import { downloadWeekPdf, googleCalendarUrl, downloadSessionIcs, sessionStart } from "../lib/exports";
import PaceCalculator from "./PaceCalculator";
import FeedbackBanner from "./FeedbackBanner";

const TYPE_LABEL = { running: "Course", strength: "Force", hyrox_wod: "WOD HYROX" };
const TYPE_COLOR = { running: "bg-blue-500/15 text-blue-400", strength: "bg-amber-500/15 text-amber-400", hyrox_wod: "bg-primary/15 text-primary" };
const TYPE_ICON = { running: Footprints, strength: Dumbbell, hyrox_wod: Flame };

function ProgramSkeleton() {
  return (
    <div className="animate-pulse space-y-4" data-testid="program-skeleton">
      <div className="h-16 bg-secondary/60 rounded-lg" />
      <div className="h-56 bg-secondary/60 rounded-xl" />
      <div className="flex gap-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-9 w-14 bg-secondary/60 rounded-md" />)}</div>
      <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 bg-secondary/60 rounded-lg" />)}</div>
    </div>
  );
}
const ISSUE_REASONS = [
  { id: "injury", label: "Blessure" },
  { id: "fatigue", label: "Fatigue / surmenage" },
  { id: "unavailable", label: "Indisponibilité" },
];
const ISSUE_LABEL = { injury: "Blessure", fatigue: "Fatigue / surmenage", unavailable: "Indisponibilité" };

const PHASE_NUTRITION = {
  base: {
    label: "Base",
    focus: "Construire l'endurance de fond sans excès calorique. Priorité à la régularité et à la qualité des aliments.",
    pre: "Glucides complexes + protéines légères 90 min avant (avoine, banane, café). 400-500 ml d'eau.",
    during: "Eau simple sur les séances < 60 min ; électrolytes si forte chaleur.",
    post: "Repas complet dans les 60 min : glucides + 25-30 g de protéines (riz/poulet/légumes).",
    hydration: "35 ml/kg/jour de base, +500 ml par heure d'entraînement.",
  },
  build: {
    label: "Build",
    focus: "Soutenir la montée en charge : apport protéique élevé et glucides autour des grosses séances.",
    pre: "Repas riche en glucides 2-3 h avant les séances intenses + en-cas (banane/dattes) 45 min avant.",
    during: "Glucides rapides (25-40 g/h) + électrolytes sur les WOD longs et le fractionné.",
    post: "Ratio 3:1 glucides/protéines dans les 30-45 min. Vise 1,6-2 g de protéines/kg/jour.",
    hydration: "Monte à ~40 ml/kg/jour ; surveille la couleur des urines.",
  },
  deload: {
    label: "Deload",
    focus: "Semaine de décharge : réduis légèrement les calories, favorise digestion et récupération.",
    pre: "Repas plus légers, moins de glucides rapides (volume d'entraînement réduit).",
    during: "Eau simple suffit sur la plupart des séances allégées.",
    post: "Priorité aux protéines et aux micronutriments (légumes, fruits, oméga-3).",
    hydration: "Maintiens l'hydratation habituelle ; profites-en pour soigner ton sommeil.",
  },
  peak: {
    label: "Peak",
    focus: "Pic de charge : maximise la disponibilité en glycogène pour tenir l'intensité.",
    pre: "Charge glucidique renforcée avant les séances clés (pâtes/riz/patate douce).",
    during: "Glucides 40-60 g/h + électrolytes systématiques sur les simulations HYROX.",
    post: "Recharge rapide (glucides + protéines) puis repas complet ; ne saute aucun apport.",
    hydration: "~45 ml/kg/jour ; pré-hydrate-toi avant les grosses séances.",
  },
  taper: {
    label: "Taper / Affûtage",
    focus: "Dernière semaine : baisse du volume, maintien de l'intensité, recharge glucidique progressive.",
    pre: "Recharge glucidique sur les 2-3 jours avant la course, sans nouveauté alimentaire.",
    during: "Teste ta stratégie de course : gels/électrolytes déjà éprouvés à l'entraînement.",
    post: "Repas digestes et familiers ; privilégie sommeil et gestion du stress.",
    hydration: "Hydratation + sodium réguliers ; vise des urines claires la veille.",
  },
};

function phaseKey(phase) {
  const p = (phase || "").toLowerCase();
  if (p.includes("taper") || p.includes("affût")) return "taper";
  if (p.includes("peak") || p.includes("pic")) return "peak";
  if (p.includes("deload") || p.includes("décharge")) return "deload";
  if (p.includes("build")) return "build";
  return "base";
}

function Celebration({ data, onDone }) {
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [data, onDone]);
  if (!data) return null;
  const colors = ["#FF3B30", "#3B82F6", "#F59E0B", "#22C55E", "#FFFFFF"];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="celebration-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background/70 backdrop-blur-sm px-6" onClick={onDone}>
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.span key={i}
            initial={{ y: -40, x: `${(i * 2.5) % 100}vw`, opacity: 1, rotate: 0 }}
            animate={{ y: "110vh", rotate: 360 }}
            transition={{ duration: 2.2 + (i % 5) * 0.3, delay: (i % 10) * 0.05, ease: "easeIn" }}
            style={{ position: "absolute", width: 9, height: 9, borderRadius: 2, background: colors[i % colors.length] }} />
        ))}
      </div>
      <motion.div initial={{ scale: 0.8, y: 10 }} animate={{ scale: 1, y: 0 }} className="relative text-center">
        <p className="font-display text-3xl sm:text-4xl mb-2">{data.title}</p>
        <p className="text-sm text-muted-foreground">{data.subtitle}</p>
      </motion.div>
    </motion.div>
  );
}

function NutritionTab({ program, profile }) {
  const weeks = program?.weeks || [];
  const currentIdx = useMemo(() => {
    if (!program?.created_at || !weeks.length) return 0;
    const days = Math.floor((Date.now() - new Date(program.created_at).getTime()) / 86400000);
    return Math.max(0, Math.min(weeks.length - 1, Math.floor(days / 7)));
  }, [program, weeks.length]);
  const [sel, setSel] = useState(currentIdx);
  useEffect(() => { setSel(currentIdx); }, [currentIdx]);

  const week = weeks[sel];
  const rules = PHASE_NUTRITION[phaseKey(week?.phase)];

  const [targets, setTargets] = useState([]);
  const [recipes, setRecipes] = useState({});
  const [recipeLoading, setRecipeLoading] = useState(false);
  useEffect(() => {
    api.get("/nutrition/targets").then((r) => setTargets(r.data?.weeks || [])).catch(() => {});
  }, []);
  const wt = targets.find((t) => t.week_number === week?.week_number);
  const rec = recipes[week?.week_number];
  const loadRecipes = async (regenerate = false) => {
    setRecipeLoading(true);
    try {
      const { data } = await api.post("/nutrition/recipes", { week_number: week.week_number, regenerate });
      setRecipes((p) => ({ ...p, [week.week_number]: data }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setRecipeLoading(false); }
  };
  const macroBar = wt ? (() => {
    const p = (wt.protein_g || 0) * 4, c = (wt.carbs_g || 0) * 4, f = (wt.fat_g || 0) * 9;
    const tot = p + c + f || 1;
    return { p: (p / tot) * 100, c: (c / tot) * 100, f: (f / tot) * 100 };
  })() : null;

  return (
    <div className="space-y-4">
      {weeks.length > 0 ? (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 hyrox-scroll" data-testid="nutrition-week-selector">
            {weeks.map((w, i) => (
              <button key={i} data-testid={`nutrition-week-${i}`} onClick={() => setSel(i)}
                className={`shrink-0 px-3 h-9 rounded-md text-xs font-semibold transition-colors ${i === sel ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                S{w.week_number}{i === currentIdx ? " •" : ""}
              </button>
            ))}
          </div>
          <div className="bg-card border border-border rounded-lg p-5" data-testid="nutrition-week-card">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Semaine {week.week_number}</p>
              <Badge className="bg-primary/15 text-primary border-0 shrink-0">{rules.label}{sel === currentIdx ? " · en cours" : ""}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{rules.focus}</p>
            {week.nutrition && (
              <div className="mt-3 bg-primary/5 border border-primary/30 rounded-md p-3" data-testid="nutrition-ai-note">
                <p className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><Sparkles size={13} /> Reco IA de la semaine</p>
                <p className="text-sm text-muted-foreground">{week.nutrition}</p>
              </div>
            )}
          </div>
          {wt && wt.kcal && (
            <div className="bg-card border border-border rounded-lg p-5" data-testid="nutrition-macros-card">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-primary">Tes cibles du jour</p>
                <span className="font-display text-xl" data-testid="nutrition-kcal">{wt.kcal} kcal</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden mb-3" data-testid="macro-bar">
                <div className="bg-blue-400" style={{ width: `${macroBar.c}%` }} />
                <div className="bg-primary" style={{ width: `${macroBar.p}%` }} />
                <div className="bg-amber-400" style={{ width: `${macroBar.f}%` }} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="font-semibold" data-testid="macro-carbs">{wt.carbs_g} g</p><p className="text-xs text-blue-400">Glucides</p></div>
                <div><p className="font-semibold" data-testid="macro-protein">{wt.protein_g} g</p><p className="text-xs text-primary">Protéines</p></div>
                <div><p className="font-semibold" data-testid="macro-fat">{wt.fat_g} g</p><p className="text-xs text-amber-400">Lipides</p></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">Calculé sur ta morphologie (Mifflin-St Jeor) et le volume de la semaine, ajusté à la phase {rules.label}.</p>
            </div>
          )}

          <div className="bg-card border border-border rounded-lg p-5" data-testid="recipes-card">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs uppercase tracking-widest text-primary">Recettes de la semaine</p>
              {rec && <button data-testid="recipes-regen-btn" onClick={() => loadRecipes(true)} disabled={recipeLoading} className="text-xs text-muted-foreground hover:text-foreground underline">Régénérer</button>}
            </div>
            {!rec ? (
              <>
                <p className="text-sm text-muted-foreground mb-3">Des recettes concrètes adaptées à tes cibles macro, tes allergies et tes goûts.</p>
                <Button data-testid="generate-recipes-btn" onClick={() => loadRecipes(false)} disabled={recipeLoading} className="w-full h-11 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold">
                  {recipeLoading ? <><RefreshCw size={15} className="animate-spin mr-2" /> Génération…</> : <><Sparkles size={15} className="mr-2" /> Générer mes recettes</>}
                </Button>
              </>
            ) : (
              <div className="space-y-3 mt-2" data-testid="recipes-list">
                {[["Petit-déjeuner", "breakfast"], ["Repas pré-séance", "pre_session"], ["Repas post-séance", "post_session"]].map(([label, key]) => {
                  const r = rec.recipes?.[key];
                  if (!r) return null;
                  return (
                    <div key={key} className="rounded-md bg-secondary/40 p-3">
                      <p className="text-[11px] uppercase tracking-widest text-primary">{label}</p>
                      <p className="font-semibold text-sm mb-1">{r.title}</p>
                      <p className="text-xs text-muted-foreground mb-1">{(r.ingredients || []).join(" · ")}</p>
                      {r.steps && <p className="text-xs text-muted-foreground italic mb-1">{r.steps}</p>}
                      <p className="text-[11px] text-muted-foreground">{r.kcal} kcal · P {r.protein_g}g · G {r.carbs_g}g · L {r.fat_g}g</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {[["Avant la séance", rules.pre], ["Pendant la séance", rules.during], ["Après (fenêtre 30-45 min)", rules.post], ["Hydratation", rules.hydration]].map(([t, d], i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-widest text-primary mb-1">{t}</p>
              <p className="text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </>
      ) : (
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-sm text-muted-foreground">Génère ton programme pour débloquer les recommandations nutritionnelles adaptées semaine par semaine.</p>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-5">
        <p className="text-xs uppercase tracking-widest text-primary mb-3">Supplémentation recommandée</p>
        <ul className="text-sm text-muted-foreground space-y-2">
          <li><span className="text-foreground font-medium">Créatine 3-5g/jour</span> — force & répétition d'efforts (sled, wall balls).</li>
          <li><span className="text-foreground font-medium">Protéine whey 20-30g</span> — récupération musculaire post-séance.</li>
          <li><span className="text-foreground font-medium">Électrolytes</span> — sur séances longues et par forte chaleur.</li>
        </ul>
        {profile.supplements && <p className="text-xs text-muted-foreground mt-3">Ta supplémentation actuelle : {profile.supplements}</p>}
      </div>
      <div className="bg-card border border-border rounded-lg p-5">
        <p className="text-xs uppercase tracking-widest text-primary mb-2">Sommeil & hydratation</p>
        <p className="text-sm text-muted-foreground">Vise {profile.sleep_hours ? `${profile.sleep_hours}h+` : "7-9h"} de sommeil. Hydratation cible : 35 ml/kg/jour, +500 ml par heure d'entraînement.</p>
      </div>
    </div>
  );
}

function paceToSec(p) {
  if (!p || typeof p !== "string" || !p.includes(":")) return null;
  const [m, s] = p.split(":").map(Number);
  if (isNaN(m) || isNaN(s)) return null;
  return m * 60 + s;
}

const REVIEW_STATIONS = ["Course / running", "SkiErg", "Sled Push", "Sled Pull", "Burpee Broad Jump", "Rowing", "Farmers Carry", "Sandbag Lunges", "Wall Balls"];
const REVIEW_DIFF = [
  { id: "too_easy", label: "Trop facile" },
  { id: "just_right", label: "Juste bien" },
  { id: "too_hard", label: "Trop dur" },
];

function WeeklyReviewDialog({ open, onClose, week, programId, existing, onSaved }) {
  const [difficulty, setDifficulty] = useState("");
  const [stations, setStations] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setDifficulty(existing?.difficulty || "");
      const st = {}; (existing?.stations || []).forEach((s) => { st[s.name] = s.difficulty; });
      setStations(st);
      setNote(existing?.note || "");
    }
  }, [open, existing]);

  const setStation = (name, diff) => setStations((p) => ({ ...p, [name]: p[name] === diff ? undefined : diff }));

  const submit = async () => {
    setSaving(true);
    try {
      const stationsArr = Object.entries(stations).filter(([, v]) => v).map(([name, difficulty]) => ({ name, difficulty }));
      await api.post("/weekly-review", { program_id: programId, week_number: week.week_number, difficulty, stations: stationsArr, note: note || undefined });
      toast.success("Bilan envoyé — ton coach l'utilisera pour ajuster ta semaine");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!week) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="weekly-review-dialog" className="bg-card border-border w-[calc(100%-1.5rem)] max-w-md max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Comment s'est passée ta semaine ?</DialogTitle>
          <DialogDescription className="text-muted-foreground">Semaine {week.week_number} — ton ressenti aide ton coach à calibrer la charge et le volume de la suite.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Ressenti global des entraînements</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="review-difficulty-buttons">
              {REVIEW_DIFF.map((d) => (
                <button key={d.id} data-testid={`review-diff-${d.id}`} onClick={() => setDifficulty(d.id)}
                  className={`h-12 rounded-md text-sm font-semibold transition-colors active:scale-95 ${difficulty === d.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{d.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Exercices précis (optionnel)</Label>
            <div className="space-y-1.5" data-testid="review-stations">
              {REVIEW_STATIONS.map((name) => (
                <div key={name} className="flex items-center justify-between gap-2 bg-secondary/40 rounded-md px-3 py-1.5">
                  <span className="text-sm truncate">{name}</span>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setStation(name, "too_easy")} className={`text-xs px-2 h-7 rounded font-semibold ${stations[name] === "too_easy" ? "bg-blue-500 text-white" : "bg-secondary text-muted-foreground"}`}>Facile</button>
                    <button onClick={() => setStation(name, "too_hard")} className={`text-xs px-2 h-7 rounded font-semibold ${stations[name] === "too_hard" ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>Dur</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Commentaire (optionnel)</Label>
            <Textarea data-testid="review-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : jambes lourdes en fin de semaine, envie de plus de force…" className="text-base bg-secondary border-border mt-1 min-h-[70px]" />
          </div>
          <Button data-testid="review-submit-btn" onClick={submit} disabled={saving || !difficulty} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold active:scale-95 transition-transform">
            {saving ? "…" : existing ? "Mettre à jour mon bilan" : "Envoyer mon bilan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportIssueDialog({ open, onClose, session, programId, onReported }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setReason(""); setNote(""); } }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/sessions/${session.id}/report_issue`, { program_id: programId, reason, note: note || undefined });
      toast.success("Empêchement signalé — ton coach est notifié");
      onReported();
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border w-[calc(100%-1.5rem)] max-w-md max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Signaler un empêchement</DialogTitle>
          <DialogDescription className="text-muted-foreground">« {session.title} » — ton coach sera notifié pour ajuster cette séance sans régénérer ton programme.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Motif</Label>
            <div className="grid gap-2" data-testid="issue-reason-buttons">
              {ISSUE_REASONS.map((r) => (
                <button key={r.id} data-testid={`issue-reason-${r.id}`} onClick={() => setReason(r.id)}
                  className={`h-12 rounded-md text-sm font-semibold transition-colors active:scale-95 ${reason === r.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Précisions (optionnel)</Label>
            <Textarea data-testid="issue-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : douleur au genou, déplacement pro…" className="text-base bg-secondary border-border mt-1 min-h-[70px]" />
          </div>
          <Button data-testid="issue-submit-btn" onClick={submit} disabled={saving || !reason} className="w-full h-12 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold active:scale-95 transition-transform">
            {saving ? "…" : "Envoyer au coach"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LogDialog({ open, onClose, session, programId, onLogged, stravaConnected, program, logs, onCelebrate }) {
  const [d, setD] = useState({ duration_min: "", rpe: "", weights: "", reps: "", wall_balls_unbroken: "", compromised_pace: "", bodyweight_kg: "", station_times: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [stravaCands, setStravaCands] = useState(null);
  const [stravaLoading, setStravaLoading] = useState(false);
  const [stravaSelId, setStravaSelId] = useState(null);
  useEffect(() => { if (open) { setD({ duration_min: "", rpe: "", weights: "", reps: "", wall_balls_unbroken: "", compromised_pace: "", bodyweight_kg: "", station_times: "", notes: "" }); setStravaCands(null); setStravaSelId(null); } }, [open]);

  const importStrava = async () => {
    setStravaLoading(true);
    try {
      const { data } = await api.post("/integrations/strava/match", { program_id: programId, session_id: session.id });
      const cands = data.candidates || [];
      setStravaCands(cands);
      if (cands.length === 0) { toast.info("Aucune course Strava trouvée."); return; }
      const suggested = cands.find((c) => c.activity_id === data.suggested_activity_id) || cands[0];
      pickStrava(suggested);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setStravaLoading(false); }
  };

  const pickStrava = (c) => {
    setStravaSelId(c.activity_id);
    setD((p) => ({
      ...p,
      duration_min: String(c.duration_min || ""),
      compromised_pace: c.pace || "",
      distance_km: c.distance_km,
      strava_activity_id: c.activity_id,
      strava_name: c.name,
      notes: `Importé de Strava : ${c.name} (${c.distance_km} km${c.pace ? ` · ${c.pace}/km` : ""})`,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { program_id: programId };
      Object.entries(d).forEach(([k, v]) => {
        if (v === "") return;
        payload[k] = ["duration_min", "rpe", "wall_balls_unbroken", "bodyweight_kg"].includes(k) ? Number(v) : v;
      });
      await api.post(`/sessions/${session.id}/log`, payload);
      toast.success("Séance validée 💪");
      // Celebration detection (week complete / personal record)
      try {
        const paceSec = (p) => { const m = /^(\d+):(\d{1,2})$/.exec((p || "").trim()); return m ? +m[1] * 60 + +m[2] : null; };
        let cel = null;
        const wk = program?.weeks?.find((w) => w.sessions.some((s) => s.id === session.id));
        if (wk && wk.sessions.every((s) => s.completed || s.id === session.id)) {
          cel = { title: `Semaine ${wk.week_number} bouclée ! 🔥`, subtitle: "Toutes les séances validées. Continue comme ça." };
        }
        if (!cel && payload.wall_balls_unbroken) {
          const prev = Math.max(0, ...(logs || []).map((l) => l.wall_balls_unbroken || 0));
          if (payload.wall_balls_unbroken > prev) cel = { title: "Record wall balls ! 🎯", subtitle: `${payload.wall_balls_unbroken} unbroken — nouveau perso.` };
        }
        if (!cel && payload.compromised_pace) {
          const cur = paceSec(payload.compromised_pace);
          const prevPaces = (logs || []).map((l) => paceSec(l.compromised_pace)).filter(Boolean);
          if (cur && prevPaces.length && cur < Math.min(...prevPaces)) cel = { title: "Record d'allure ! ⚡", subtitle: `${payload.compromised_pace}/km — ta meilleure allure loggée.` };
        }
        if (cel && onCelebrate) onCelebrate(cel);
      } catch { /* non-blocking */ }
      onLogged();
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <DialogHeader><DialogTitle className="font-display text-2xl">{session.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {session.type === "running" && stravaConnected && (
            <div className="rounded-md border border-[#FC4C02]/40 bg-[#FC4C02]/5 p-3">
              {!stravaCands ? (
                <button data-testid="strava-import-btn" onClick={importStrava} disabled={stravaLoading}
                  className="w-full flex items-center justify-center gap-2 h-10 rounded-md text-sm font-semibold text-[#FC4C02] hover:bg-[#FC4C02]/10 transition-colors">
                  {stravaLoading ? <RefreshCw size={15} className="animate-spin" /> : <Activity size={15} />} Importer depuis Strava
                </button>
              ) : (
                <div className="space-y-2" data-testid="strava-candidates">
                  <p className="text-xs uppercase tracking-widest text-[#FC4C02]">Tes courses Strava récentes — choisis la bonne</p>
                  {stravaCands.length === 0 && <p className="text-xs text-muted-foreground">Aucune course trouvée. Saisis manuellement.</p>}
                  <div className="max-h-52 overflow-y-auto space-y-2 pr-1">
                    {stravaCands.map((c) => {
                      const sel = c.activity_id === stravaSelId;
                      return (
                        <button key={c.activity_id} data-testid={`strava-cand-${c.activity_id}`} onClick={() => pickStrava(c)}
                          className={`w-full text-left rounded-md p-2.5 transition-colors border ${sel ? "border-[#FC4C02] bg-[#FC4C02]/10" : "border-transparent bg-secondary/60 hover:bg-secondary"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">{c.name}</p>
                            {sel && <span className="text-[10px] uppercase tracking-wider text-[#FC4C02] shrink-0">Sélectionnée</span>}
                          </div>
                          <p className="text-xs text-muted-foreground">{c.distance_km} km · {c.duration_min} min · {c.pace ? `${c.pace}/km` : "—"} · {new Date(c.start_date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</p>
                        </button>
                      );
                    })}
                  </div>
                  <button data-testid="strava-cancel-btn" onClick={() => { setStravaCands(null); setStravaSelId(null); }} className="text-xs text-muted-foreground hover:text-foreground underline">Fermer la liste</button>
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Ressenti (RPE)</Label>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5" data-testid="log-rpe-buttons">
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} onClick={() => setD((p) => ({ ...p, rpe: n }))}
                  className={`h-12 rounded-md text-base font-bold transition-colors active:scale-95 ${d.rpe === n ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{n}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Durée (min)</Label><Input data-testid="log-duration" type="number" value={d.duration_min} onChange={(e) => setD({ ...d, duration_min: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Poids de corps</Label><Input data-testid="log-bw" type="number" value={d.bodyweight_kg} onChange={(e) => setD({ ...d, bodyweight_kg: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Wall balls unbroken</Label><Input data-testid="log-wb" type="number" value={d.wall_balls_unbroken} onChange={(e) => setD({ ...d, wall_balls_unbroken: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Pace compromised</Label><Input data-testid="log-pace" placeholder="4:15" value={d.compromised_pace} onChange={(e) => setD({ ...d, compromised_pace: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Charges</Label><Input data-testid="log-weights" placeholder="squat 90kg…" value={d.weights} onChange={(e) => setD({ ...d, weights: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Reps</Label><Input data-testid="log-reps" value={d.reps} onChange={(e) => setD({ ...d, reps: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Temps par station (simulation)</Label><Input data-testid="log-stations" placeholder="ski 1:20, sled 2:00…" value={d.station_times} onChange={(e) => setD({ ...d, station_times: e.target.value })} className="h-12 text-base bg-secondary border-border mt-1" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label><Textarea data-testid="log-notes" value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} className="text-base bg-secondary border-border mt-1 min-h-[70px]" /></div>
          <Button data-testid="log-save-btn" onClick={save} disabled={saving} className="w-full h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md font-semibold active:scale-95 transition-transform">
            {saving ? "…" : "Valider la séance"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Chart({ title, data, dataKey, color, unit, invert }) {
  if (!data.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">{title}</h4>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#27272A" strokeOpacity={0.4} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} reversed={invert} domain={["auto", "auto"]} />
          <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A", borderRadius: 8, color: "#fff" }} formatter={(v) => [`${v}${unit || ""}`, ""]} />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 3, fill: color }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AthleteHome() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sub = user.subscription || {};
  const hasAccess = user.role === "coach" ? true : !!sub.has_access;

  const manageSubscription = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  };
  const [tab, setTab] = useState("program");
  useEffect(() => { track("tab_view", { tab: "program" }); }, []);
  const loadReviews = useCallback(() => { api.get("/weekly-reviews").then((r) => setReviews(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadReviews(); }, [loadReviews]);
  const [program, setProgram] = useState(null);
  const [logs, setLogs] = useState([]);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [genStatus, setGenStatus] = useState("idle"); // idle | generating | error
  const [elapsed, setElapsed] = useState(0);
  const [logSession, setLogSession] = useState(null);
  const [issueSession, setIssueSession] = useState(null);
  const [reviewWeek, setReviewWeek] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [focusId, setFocusId] = useState(null);
  const [browseWeek, setBrowseWeek] = useState(0);
  const [checkedMap, setCheckedMap] = useState({});
  const [stravaConnected, setStravaConnected] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const pollingRef = useRef(false);
  const startTimeRef = useRef(0);

  const stopTimers = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    pollRef.current = null;
    timerRef.current = null;
    pollingRef.current = false;
  }, []);

  const startPolling = useCallback(() => {
    setGenStatus("generating");
    if (pollingRef.current) return; // already polling — avoid duplicate cycles
    pollingRef.current = true;
    startTimeRef.current = Date.now();
    if (!timerRef.current) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    }
    const poll = async () => {
      if (Date.now() - startTimeRef.current > 300000) { // 5 min safety timeout
        stopTimers();
        setGenStatus("error");
        return;
      }
      try {
        const { data } = await api.get("/programs/me");
        if (data && data.status === "generating") { pollRef.current = setTimeout(poll, 3000); return; }
        stopTimers();
        if (data && data.status === "error") { setGenStatus("error"); return; }
        if (data && data.id && ["draft", "active"].includes(data.status)) {
          setProgram(data);
          setGenStatus("idle");
          toast.success("Programme généré par l'IA !", { id: "gen-done" });
        } else {
          setGenStatus("idle");
        }
      } catch { pollRef.current = setTimeout(poll, 3000); }
    };
    pollRef.current = setTimeout(poll, 2000);
  }, [stopTimers]);

  const load = useCallback(async () => {
    setLoading(true);
    let hasProg = false;
    try {
      const [p, l, pr] = await Promise.allSettled([
        api.get("/programs/me"), api.get("/logs/me"), api.get("/profile"),
      ]);
      api.get("/integrations/strava/status").then((r) => setStravaConnected(!!r.data?.connected)).catch(() => {});
      const pdata = p.status === "fulfilled" ? p.value.data : null;
      setLogs(l.status === "fulfilled" ? (l.value.data || []) : []);
      setProfile(pr.status === "fulfilled" ? (pr.value.data || {}) : {});
      hasProg = !!(pdata && pdata.id && ["draft", "active"].includes(pdata.status));
      setProgram(hasProg ? pdata : null);
      if (pdata && pdata.status === "generating") startPolling();
    } finally {
      setLoading(false);
    }
    return hasProg;
  }, [startPolling]);

  const generate = useCallback(async () => {
    setElapsed(0);
    setGenStatus("generating");
    try {
      await api.post("/programs/generate", {});
      startPolling();
    } catch (err) {
      stopTimers();
      setGenStatus("error");
      toast.error(formatError(err.response?.data?.detail) || err.message);
    }
  }, [startPolling, stopTimers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hasProg = await load();
      if (cancelled) return;
      if (params.get("generate") === "1") {
        // Strip the param immediately so a page reload can NEVER re-trigger generation.
        const url = new URL(window.location.href);
        url.searchParams.delete("generate");
        window.history.replaceState({}, "", url.pathname + url.search);
        // Only auto-generate if NO program document exists at all (not even one currently generating).
        if (!hasProg) {
          try {
            const { data } = await api.get("/programs/me");
            if (!cancelled && (!data || !data.id)) generate();
          } catch { /* ignore */ }
        }
      }
    })();
    return () => { cancelled = true; stopTimers(); };
    // eslint-disable-next-line
  }, []);

  const flat = useMemo(() => {
    if (!program?.weeks) return [];
    const arr = [];
    program.weeks.forEach((w) => (w.sessions || []).forEach((s) => arr.push({ w, s, start: sessionStart(program, w, s) })));
    return arr.sort((a, b) => a.start - b.start);
  }, [program]);

  const defaultFocus = useMemo(() => {
    if (!flat.length) return null;
    const now = new Date();
    const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const dayKey = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const sameDay = flat.filter((f) => dayKey(f.start) === t0);
    let pick = sameDay.find((f) => !f.s.completed) || sameDay[0];
    if (!pick) pick = flat.find((f) => dayKey(f.start) >= t0 && !f.s.completed);
    if (!pick) pick = flat.find((f) => !f.s.completed);
    if (!pick) pick = flat[flat.length - 1];
    const weekIdx = program.weeks.findIndex((w) => w === pick.w);
    return { id: pick.s.id, weekIdx: weekIdx < 0 ? 0 : weekIdx };
  }, [flat, program]);

  useEffect(() => {
    if (defaultFocus) { setFocusId(defaultFocus.id); setBrowseWeek(defaultFocus.weekIdx); }
    // eslint-disable-next-line
  }, [defaultFocus?.id]);

  useEffect(() => {
    if (program?.weeks) {
      const m = {};
      program.weeks.forEach((w) => (w.sessions || []).forEach((s) => { if (s.checked) m[s.id] = s.checked; }));
      setCheckedMap(m);
    }
  }, [program]);

  const toggleCheck = (session, idx) => {
    setCheckedMap((prev) => {
      const cur = new Set(prev[session.id] || []);
      cur.has(idx) ? cur.delete(idx) : cur.add(idx);
      const arr = [...cur];
      api.post(`/sessions/${session.id}/check`, { program_id: program.id, checked: arr }).catch(() => {});
      if (session.main && arr.length === session.main.length && !session.completed) {
        setTimeout(() => setLogSession(session), 350);
      }
      return { ...prev, [session.id]: arr };
    });
  };

  const wbData = logs.filter((l) => l.wall_balls_unbroken).map((l, i) => ({ label: `S${i + 1}`, value: l.wall_balls_unbroken }));  const paceData = logs.filter((l) => paceToSec(l.compromised_pace)).map((l, i) => ({ label: `S${i + 1}`, value: paceToSec(l.compromised_pace) }));
  const bwData = logs.filter((l) => l.bodyweight_kg).map((l, i) => ({ label: `S${i + 1}`, value: l.bodyweight_kg }));

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="flex items-center justify-between px-5 md:px-8 py-4 border-b border-border sticky top-0 bg-background/95 backdrop-blur z-20">
        <div className="flex items-center gap-2">
          <Flame className="text-primary" size={22} strokeWidth={2.5} />
          <span className="font-display text-xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground hidden sm:block">{user.name}</span>
          <PaceCalculator />
          <Button data-testid="settings-btn" variant="ghost" size="icon" onClick={() => nav("/settings")}><SettingsIcon size={18} /></Button>
          <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }}><LogOut size={18} /></Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6">
        {profile.target_time && (
          <div className="bg-card border border-border rounded-lg p-5 mb-6 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Objectif</p>
              <p className="font-display text-3xl text-primary">{profile.target_time}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Course</p>
              <p className="text-lg font-semibold">{formatFrDate(profile.race_date)}</p>
            </div>
          </div>
        )}

        {sub.status === "trialing" && sub.has_access && (
          <div data-testid="trial-banner" className="bg-primary/10 border border-primary/40 rounded-lg p-4 mb-6 flex items-center justify-between gap-3">
            <p className="text-sm"><Sparkles size={15} className="inline text-primary mr-1" /> Essai gratuit — expire le <span className="font-semibold">{formatFrDate(sub.trial_end)}</span></p>
            <Button data-testid="trial-subscribe-btn" size="sm" onClick={() => nav("/pricing")} className="bg-primary hover:bg-primary/90 text-white shrink-0">S'abonner</Button>
          </div>
        )}
        {sub.status === "past_due" && (
          <div data-testid="payment-failed-banner" className="bg-destructive/15 border border-destructive/50 rounded-lg p-4 mb-6 flex items-center justify-between gap-3">
            <p className="text-sm"><Lock size={15} className="inline text-destructive mr-1" />
              {sub.grace_until && new Date(sub.grace_until) > new Date()
                ? <>Paiement échoué — mets à jour ton moyen de paiement avant le <span className="font-semibold">{formatFrDate(sub.grace_until)}</span> pour garder l'accès.</>
                : <>Paiement échoué et période de grâce terminée — mets à jour ton moyen de paiement pour réactiver ton accès.</>}
            </p>
            {sub.manageable && (
              <Button data-testid="update-payment-btn" size="sm" onClick={manageSubscription} className="bg-destructive hover:bg-destructive/90 text-white shrink-0">Mettre à jour</Button>
            )}
          </div>
        )}
        {sub.status === "active" && (
          <div data-testid="active-sub-banner" className="bg-secondary/60 border border-border rounded-lg p-4 mb-6 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Abonnement actif — <span className="font-semibold text-foreground">{sub.plan === "coaching" ? "Coaching complet" : "Programme seul"}</span></p>
            {sub.manageable && (
              <Button data-testid="manage-sub-btn" size="sm" variant="outline" onClick={manageSubscription} className="border-border bg-transparent hover:bg-secondary shrink-0">Gérer mon abonnement</Button>
            )}
          </div>
        )}
        {sub.status === "canceling" && (
          <div data-testid="canceling-sub-banner" className="bg-secondary/60 border border-border rounded-lg p-4 mb-6 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Abonnement actif jusqu'au <span className="font-semibold text-foreground">{formatFrDate(sub.current_period_end)}</span> — résiliation programmée.</p>
            {sub.manageable && (
              <Button data-testid="manage-sub-btn" size="sm" variant="outline" onClick={manageSubscription} className="border-border bg-transparent hover:bg-secondary shrink-0">Réactiver / gérer</Button>
            )}
          </div>
        )}


        {/* Tabs */}
        <FeedbackBanner context="app" />
        <div className="flex gap-2 mb-6">
          {[["program", "Programme", Dumbbell], ["progress", "Progression", LineIcon], ["nutrition", "Nutrition", Apple]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`tab-${id}`} onClick={() => { setTab(id); track("tab_view", { tab: id }); }}
              className={`flex-1 flex items-center justify-center gap-2 h-12 rounded-md text-sm font-semibold transition-colors ${tab === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
              <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {loading && tab === "program" && <ProgramSkeleton />}
        {loading && tab !== "program" && <p className="text-muted-foreground text-center py-10">Chargement…</p>}

        {/* PROGRAM */}
        {!loading && tab === "program" && (
          <div>
            {!hasAccess && !program ? (
              <div data-testid="subscription-required-card" className="bg-card border border-primary/40 rounded-lg p-8 text-center">
                <Lock className="text-primary mx-auto mb-3" size={32} />
                <h3 className="font-display text-2xl mb-2">Abonnement requis</h3>
                <p className="text-muted-foreground mb-5 text-sm">
                  {sub.status === "expired" ? "Ton essai gratuit est terminé." : "Un abonnement actif est nécessaire"} — abonne-toi pour générer et suivre ton programme HYROX.
                </p>
                <Button data-testid="subscribe-cta-btn" onClick={() => nav("/pricing")} className="bg-primary hover:bg-primary/90 text-white rounded-md h-13 py-3 px-8 font-semibold">
                  Voir les formules
                </Button>
              </div>
            ) : genStatus === "generating" && !program ? (
              <div data-testid="generating-card" className="bg-card border border-border rounded-lg p-8 text-center">
                <RefreshCw className="text-primary mx-auto mb-4 animate-spin" size={36} />
                <h3 className="font-display text-2xl mb-2">Génération IA en cours</h3>
                {(() => {
                  const steps = [
                    "Analyse de ton profil et de tes disponibilités…",
                    "Planification de la périodisation (Base → Build → Peak → Taper)…",
                    "Construction en parallèle de tes semaines d'entraînement…",
                    "Ajustement des charges aux poids de compétition HYROX…",
                    "Personnalisation running, force, WOD et nutrition…",
                    "Finalisation de ton plan personnalisé…",
                  ];
                  const idx = Math.min(steps.length - 1, Math.floor(elapsed / 7));
                  return <p data-testid="gen-step-message" className="text-primary mb-1 text-sm font-medium">{steps[idx]}</p>;
                })()}
                <p className="text-muted-foreground mb-5 text-xs">Cela prend quelques minutes — tu peux laisser cette page ouverte, on t'avertit dès que c'est prêt.</p>
                <Progress data-testid="generation-progress" value={Math.min(95, Math.round((elapsed / 60) * 100))} className="h-2 bg-secondary" />
              </div>
            ) : genStatus === "error" ? (
              <div data-testid="generation-error-card" className="bg-card border border-primary/40 rounded-lg p-8 text-center">
                <AlertTriangle className="text-primary mx-auto mb-3" size={32} />
                <h3 className="font-display text-2xl mb-2">Échec de la génération</h3>
                <p className="text-muted-foreground mb-5 text-sm">La génération IA n'a pas abouti. Vérifie ta connexion et réessaie.</p>
                <Button data-testid="retry-generate-btn" onClick={generate} className="bg-primary hover:bg-primary/90 text-white rounded-md h-13 py-3 px-8 font-semibold">
                  <RefreshCw size={16} className="mr-2" /> Réessayer
                </Button>
              </div>
            ) : !program ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Zap className="text-primary mx-auto mb-3" size={32} />
                <h3 className="font-display text-2xl mb-2">Pas encore de programme</h3>
                <p className="text-muted-foreground mb-5 text-sm">
                  {user.has_profile ? "Génère ton plan HYROX personnalisé par IA (~1-2 min)." : "Complète ton profil pour générer ton plan."}
                </p>
                {user.has_profile ? (
                  <Button data-testid="generate-btn" onClick={generate} className="bg-primary hover:bg-primary/90 text-white rounded-md h-13 py-3 px-8 font-semibold">
                    Générer mon programme
                  </Button>
                ) : (
                  <Button data-testid="goto-onboarding-btn" onClick={() => nav("/onboarding")} className="bg-primary hover:bg-primary/90 text-white rounded-md h-13 py-3 px-8 font-semibold">Compléter mon profil</Button>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div>
                    <h2 className="font-display text-2xl leading-tight">{program.name}</h2>
                    <Badge className={`mt-2 ${program.status === "active" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {program.status === "active" ? "Validé par le coach" : "En attente de validation"}
                    </Badge>
                  </div>
                  <Button data-testid="regenerate-btn" variant="outline" size="sm" onClick={generate} disabled={genStatus === "generating"} className="border-border bg-transparent hover:bg-secondary shrink-0">
                    <RefreshCw size={14} className={genStatus === "generating" ? "animate-spin" : ""} />
                  </Button>
                </div>

                {(() => {
                  const focus = flat.find((f) => f.s.id === focusId) || flat[0];
                  if (!focus) return null;
                  const s = focus.s, w = focus.w;
                  const Icon = TYPE_ICON[s.type] || Dumbbell;
                  const checked = checkedMap[s.id] || [];
                  const now = new Date();
                  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                  const d0 = new Date(focus.start.getFullYear(), focus.start.getMonth(), focus.start.getDate()).getTime();
                  const focusLabel = d0 === t0 ? "Séance du jour" : (d0 > t0 ? "Prochaine séance" : (s.completed ? "Séance" : "Séance à rattraper"));
                  return (
                    <motion.div key={s.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="session-hero"
                      className="bg-card border border-primary/30 rounded-xl p-5 mb-6">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${TYPE_COLOR[s.type] || "bg-secondary"}`}><Icon size={20} /></span>
                          <div className="min-w-0">
                            <p className="text-xs uppercase tracking-widest text-primary font-semibold">{focusLabel}</p>
                            <p className="text-xs text-muted-foreground truncate">{s.day} · {s.time_slot} · Semaine {w.week_number}</p>
                          </div>
                        </div>
                        {s.completed ? <CheckCircle2 className="text-green-400 shrink-0" size={22} /> : <Circle className="text-muted-foreground shrink-0" size={22} />}
                      </div>
                      <h3 className="font-display text-2xl leading-tight mb-3">{s.title}</h3>
                      {s.regenerated_at && (
                        <div data-testid={`regen-badge-${s.id}`} className="mb-3 inline-flex items-center gap-1.5 text-xs text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1">
                          <Sparkles size={12} /> Séance régénérée par ton coach · {formatFrDate(s.regenerated_at)}
                        </div>
                      )}
                      {s.completed && s.log && (
                        <div data-testid={`session-results-${s.id}`} className="mb-3 rounded-lg border border-green-500/30 bg-green-500/5 p-3">
                          <p className="text-xs uppercase tracking-widest text-green-400 mb-2 flex items-center gap-1.5">
                            <CheckCircle2 size={13} /> Tes résultats
                            {s.log.strava_activity_id && <span className="ml-1 inline-flex items-center gap-1 text-[#FC4C02]"><Activity size={11} /> Strava</span>}
                          </p>
                          <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                            {s.log.duration_min ? <div><span className="text-muted-foreground text-xs block">Durée</span><span className="font-semibold">{s.log.duration_min} min</span></div> : null}
                            {s.log.compromised_pace ? <div><span className="text-muted-foreground text-xs block">Allure</span><span className="font-semibold">{s.log.compromised_pace}/km</span></div> : null}
                            {s.log.distance_km ? <div><span className="text-muted-foreground text-xs block">Distance</span><span className="font-semibold">{s.log.distance_km} km</span></div> : null}
                            {s.log.rpe ? <div><span className="text-muted-foreground text-xs block">RPE</span><span className="font-semibold">{s.log.rpe}/10</span></div> : null}
                            {s.log.wall_balls_unbroken ? <div><span className="text-muted-foreground text-xs block">Wall balls</span><span className="font-semibold">{s.log.wall_balls_unbroken}</span></div> : null}
                            {s.log.weights ? <div><span className="text-muted-foreground text-xs block">Charges</span><span className="font-semibold">{s.log.weights}</span></div> : null}
                          </div>
                          {s.log.notes && <p className="text-xs text-muted-foreground mt-2">{s.log.notes}</p>}
                        </div>
                      )}
                      <div className="space-y-3 text-sm">
                        {s.completed && s.log && <p className="text-xs uppercase tracking-widest text-muted-foreground">Plan prévu</p>}
                        {s.warmup && <div><p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Échauffement</p><p className="text-muted-foreground">{s.warmup}</p></div>}
                        {s.main && s.main.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Corps de séance</p>
                            <div className="space-y-1.5">
                              {s.main.map((m, mi) => (
                                <button key={mi} data-testid={`ex-check-${s.id}-${mi}`} onClick={() => toggleCheck(s, mi)}
                                  className="w-full flex items-start gap-3 text-left rounded-md p-2.5 bg-secondary/40 hover:bg-secondary/70 transition-colors active:scale-[0.99]">
                                  <span className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center shrink-0 border ${checked.includes(mi) ? "bg-primary border-primary" : "border-border"}`}>
                                    {checked.includes(mi) && <Check size={14} className="text-white" />}
                                  </span>
                                  <span className={checked.includes(mi) ? "line-through text-muted-foreground" : "text-foreground"}>{m}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {s.finisher && <div><p className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5"><Flame size={12} /> Finisher</p><p className="text-muted-foreground">{s.finisher}</p></div>}
                        {s.recovery && <div><p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Récupération</p><p className="text-muted-foreground">{s.recovery}</p></div>}
                      </div>
                      <Button data-testid={`log-session-btn-${s.id}`} onClick={() => setLogSession(s)}
                        className={`w-full mt-4 h-12 rounded-md font-semibold active:scale-95 transition-transform ${s.completed ? "bg-secondary hover:bg-secondary/80 text-foreground" : "bg-primary hover:bg-primary/90 text-white"}`}>
                        {s.completed ? "Re-logger" : "Logger cette séance"}
                      </Button>
                      {s.issue ? (
                        <div data-testid={`issue-badge-${s.id}`} className="mt-2 flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
                          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                          <span>Empêchement signalé — {ISSUE_LABEL[s.issue.reason] || s.issue.reason}{s.issue.note ? ` · ${s.issue.note}` : ""}. Ton coach est informé.</span>
                        </div>
                      ) : !s.completed ? (
                        <Button variant="outline" data-testid={`report-issue-btn-${s.id}`} onClick={() => setIssueSession(s)}
                          className="w-full mt-2 h-10 rounded-md text-sm border-border bg-transparent hover:bg-secondary text-muted-foreground active:scale-95 transition-transform">
                          <AlertTriangle size={14} className="mr-1.5" /> Signaler un empêchement
                        </Button>
                      ) : null}
                      <div className="flex gap-2 mt-2">
                        <a data-testid={`gcal-btn-${s.id}`} href={googleCalendarUrl(program, w, s)} target="_blank" rel="noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-medium border border-border bg-transparent hover:bg-secondary text-muted-foreground transition-colors"><CalendarPlus size={13} /> Google Agenda</a>
                        <button data-testid={`ics-btn-${s.id}`} onClick={() => downloadSessionIcs(program, w, s)}
                          className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md text-xs font-medium border border-border bg-transparent hover:bg-secondary text-muted-foreground transition-colors active:scale-95"><CalendarPlus size={13} /> .ics (Apple)</button>
                      </div>
                    </motion.div>
                  );
                })()}

                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Tout le programme</p>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const wk = program.weeks[browseWeek];
                      const rev = reviews.find((r) => r.week_number === wk?.week_number);
                      return (
                        <Button variant="outline" size="sm" data-testid="weekly-review-btn" onClick={() => setReviewWeek(wk)}
                          className={`text-xs h-8 bg-transparent ${rev ? "border-green-500/40 text-green-400 hover:bg-green-500/10" : "border-primary/40 text-primary hover:bg-primary/10"}`}>
                          <ClipboardList size={13} className="mr-1.5" /> {rev ? "Bilan envoyé ✓" : "Bilan de la semaine"}
                        </Button>
                      );
                    })()}
                    <Button variant="outline" size="sm" data-testid={`export-pdf-week-${browseWeek}`} onClick={() => downloadWeekPdf(program, program.weeks[browseWeek])}
                      className="border-border bg-transparent hover:bg-secondary text-xs h-8"><FileDown size={13} className="mr-1.5" /> PDF semaine</Button>
                  </div>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 mb-3 hyrox-scroll" data-testid="program-week-selector">
                  {program.weeks.map((w, wi) => {
                    const done = w.sessions.filter((x) => x.completed).length;
                    return (
                      <button key={wi} data-testid={`program-week-${wi}`} onClick={() => setBrowseWeek(wi)}
                        className={`shrink-0 px-3 h-9 rounded-md text-xs font-semibold transition-colors ${wi === browseWeek ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                        S{w.week_number} <span className="opacity-70">{done}/{w.sessions.length}</span>
                      </button>
                    );
                  })}
                </div>
                {program.weeks[browseWeek]?.focus && (
                  <p className="text-sm text-muted-foreground mb-3"><span className="uppercase tracking-widest text-primary text-xs mr-2">{program.weeks[browseWeek].phase}</span>{program.weeks[browseWeek].focus}</p>
                )}
                <div className="space-y-2">
                  {program.weeks[browseWeek]?.sessions.map((s) => {
                    const Icon = TYPE_ICON[s.type] || Dumbbell;
                    const active = s.id === focusId;
                    return (
                      <button key={s.id} data-testid={`session-row-${s.id}`} onClick={() => { setFocusId(s.id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className={`w-full flex items-center gap-3 text-left rounded-lg border p-3 transition-colors ${active ? "border-primary/50 bg-primary/5" : s.completed ? "border-green-500/30 bg-green-500/5" : "border-border bg-card hover:bg-secondary/50"}`}>
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${TYPE_COLOR[s.type] || "bg-secondary"}`}><Icon size={17} /></span>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm truncate">{s.title}</p>
                          <p className="text-xs text-muted-foreground">{s.day} · {s.time_slot}</p>
                        </div>
                        {s.issue && <AlertTriangle size={15} className="text-amber-400 shrink-0" />}
                        {s.completed ? <CheckCircle2 size={18} className="text-green-400 shrink-0" /> : <Circle size={18} className="text-muted-foreground shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* PROGRESS */}
        {!loading && tab === "progress" && (
          <div className="space-y-4">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-10">Logue des séances pour voir ta progression.</p>
            ) : (
              <>
                <Chart title="Wall balls unbroken" data={wbData} dataKey="value" color="#FF3B30" unit=" reps" />
                <Chart title="Pace compromised — sec/km (↓ mieux)" data={paceData} dataKey="value" color="#FF4500" unit="s" invert />
                <Chart title="Poids de corps (kg)" data={bwData} dataKey="value" color="#10B981" unit=" kg" />
                <div className="bg-card border border-border rounded-lg p-4" data-testid="log-history">
                  <h4 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Historique des séances loggées</h4>
                  <div className="space-y-2">
                    {[...logs].reverse().map((l, i) => (
                      <div key={l.id || i} className="flex items-center justify-between border border-border rounded-md px-3 py-2.5 bg-secondary/40">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{l.session_title || "Séance"}</p>
                          <p className="text-xs text-muted-foreground">{formatFrDate(l.logged_at)}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 text-xs">
                          {l.duration_min ? <span className="text-muted-foreground">{l.duration_min} min</span> : null}
                          {l.rpe ? (
                            <span className={`px-2 py-1 rounded font-bold ${l.rpe >= 9 ? "bg-primary/20 text-primary" : "bg-secondary text-foreground"}`}>RPE {l.rpe}</span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* NUTRITION */}
        {!loading && tab === "nutrition" && (
          <NutritionTab program={program} profile={profile} />
        )}
      </div>

      <LogDialog open={!!logSession} onClose={() => setLogSession(null)} session={logSession} programId={program?.id} onLogged={load} stravaConnected={stravaConnected} program={program} logs={logs} onCelebrate={setCelebration} />
      <Celebration data={celebration} onDone={() => setCelebration(null)} />
      <ReportIssueDialog open={!!issueSession} onClose={() => setIssueSession(null)} session={issueSession} programId={program?.id} onReported={load} />
      <WeeklyReviewDialog open={!!reviewWeek} onClose={() => setReviewWeek(null)} week={reviewWeek} programId={program?.id} existing={reviews.find((r) => r.week_number === reviewWeek?.week_number)} onSaved={loadReviews} />
    </div>
  );
}
